import { useState, useEffect } from 'react';
import type React from 'react';
import { User, Mail, Phone, Send, LogOut, MapPin, Pencil, Check, X } from 'lucide-react';
import { useTourAutoStart } from '@/hooks/useTourAutoStart';
import { customerProfileTourId, customerProfileSteps } from '@/tours/customer';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';
import { UserRoleLabels } from '@/types/enums';
import { Card, Button } from '@/components/ui';
import Input from '@/components/ui/Input';
import BranchSelector from '@/components/features/customer/BranchSelector';
import { registrationApi } from '@/services/api/registrationApi';
import { authApi } from '@/services/api/authApi';
import type { PublicBranch } from '@/services/api/registrationApi';

const TELEGRAM_BOT = import.meta.env.VITE_TELEGRAM_BOT || '';

const ProfilePage: React.FC = () => {
  useTourAutoStart({ tourId: customerProfileTourId, steps: customerProfileSteps });
  const { user, logout, setUser } = useAuthStore();
  const navigate = useNavigate();
  const [showBranchSelector, setShowBranchSelector] = useState(false);
  const [currentBranch, setCurrentBranch] = useState<PublicBranch | null>(null);
  const [editingPhone, setEditingPhone] = useState(false);
  const [phoneValue, setPhoneValue] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [phoneSaving, setPhoneSaving] = useState(false);

  useEffect(() => {
    if (!user?.branch_id) return;
    let cancelled = false;
    registrationApi.getPublicBranches().then((branches) => {
      if (cancelled) return;
      const found = branches.find((b) => b.id === user.branch_id);
      if (found) setCurrentBranch(found);
    });
    return () => { cancelled = true; };
  }, [user?.branch_id]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleBranchChanged = () => {
    setShowBranchSelector(false);
  };

  const startEditPhone = () => {
    setPhoneValue(user?.phone || '');
    setPhoneError('');
    setEditingPhone(true);
  };

  const cancelEditPhone = () => {
    setEditingPhone(false);
    setPhoneError('');
  };

  const savePhone = async () => {
    const trimmed = phoneValue.trim();
    if (!trimmed) {
      setPhoneError('Введите номер телефона');
      return;
    }
    setPhoneSaving(true);
    setPhoneError('');
    try {
      const updated = await authApi.updateProfile({ phone: trimmed });
      setUser(updated);
      setEditingPhone(false);
    } catch (err: unknown) {
      const error = err as { response?: { status?: number; data?: { detail?: string } } };
      if (error.response?.status === 409) {
        setPhoneError('Этот номер уже используется');
      } else if (error.response?.status === 422) {
        setPhoneError('Неверный формат номера');
      } else {
        setPhoneError('Ошибка сохранения');
      }
    } finally {
      setPhoneSaving(false);
    }
  };

  if (!user) return null;

  const telegramUrl = TELEGRAM_BOT
    ? `https://t.me/${TELEGRAM_BOT}?start=${user.id}`
    : null;

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Профиль</h1>
        <p className="text-sm text-gray-500 mt-1">Ваши данные</p>
      </div>

      <div data-tour="customer:profile-info">
      <Card>
        <Card.Body>
          <div className="flex items-center gap-4 mb-6 pb-6 border-b border-gray-100">
            <div className="w-16 h-16 rounded-2xl bg-primary-100 flex items-center justify-center">
              <User className="w-8 h-8 text-primary-600" />
            </div>
            <div>
              <p className="text-lg font-semibold text-gray-900">{user.full_name}</p>
              <p className="text-sm text-gray-500">{UserRoleLabels[user.role]}</p>
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gray-100 flex items-center justify-center shrink-0">
                <Mail className="w-5 h-5 text-gray-500" />
              </div>
              <div>
                <p className="text-xs text-gray-400">Email</p>
                <p className="text-sm font-medium text-gray-900">{user.email || 'Не указан'}</p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gray-100 flex items-center justify-center shrink-0">
                <Phone className="w-5 h-5 text-gray-500" />
              </div>
              {editingPhone ? (
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-gray-400 mb-1">Телефон</p>
                  <div className="flex items-center gap-2">
                    <Input
                      value={phoneValue}
                      onChange={(e) => setPhoneValue(e.target.value)}
                      placeholder="+7XXXXXXXXXX"
                      error={phoneError}
                      className="flex-1"
                      disabled={phoneSaving}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') savePhone();
                        if (e.key === 'Escape') cancelEditPhone();
                      }}
                      autoFocus
                    />
                    <button
                      onClick={savePhone}
                      disabled={phoneSaving}
                      className="p-2 rounded-lg text-emerald-600 hover:bg-emerald-50 transition-colors disabled:opacity-50"
                    >
                      <Check className="w-4 h-4" />
                    </button>
                    <button
                      onClick={cancelEditPhone}
                      disabled={phoneSaving}
                      className="p-2 rounded-lg text-gray-400 hover:bg-gray-100 transition-colors disabled:opacity-50"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex-1 flex items-center justify-between min-w-0">
                  <div>
                    <p className="text-xs text-gray-400">Телефон</p>
                    <p className="text-sm font-medium text-gray-900">
                      {user.phone || 'Не указан'}
                    </p>
                  </div>
                  <button
                    onClick={startEditPhone}
                    className="p-2 rounded-lg text-gray-400 hover:text-primary-600 hover:bg-primary-50 transition-colors"
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
          </div>

          {telegramUrl && (
            <div data-tour="customer:telegram-connect" className="mt-6 pt-6 border-t border-gray-100">
              <a
                href={telegramUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium rounded-xl bg-[#2AABEE] text-white hover:bg-[#229ED9] transition-colors shadow-sm"
              >
                <Send className="w-4 h-4" />
                Подключить Telegram
              </a>
              <p className="text-xs text-gray-400 mt-2">
                Получайте уведомления о заказах в Telegram
              </p>
            </div>
          )}
        </Card.Body>
      </Card>
      </div>

      <div className="mt-6">
        <Card>
          <Card.Body>
            <h3 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <MapPin className="w-5 h-5 text-gray-400" />
              Мой пункт выдачи
            </h3>

            {user.branch_id === null ? (
              <>
                <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 mb-4">
                  <p className="text-sm text-amber-800 font-medium">
                    Вы ещё не выбрали пункт выдачи. Выберите для получения заказов.
                  </p>
                </div>
                <BranchSelector currentBranchId={null} onBranchChanged={handleBranchChanged} />
              </>
            ) : (
              <>
                <div className="flex items-center justify-between gap-3 px-4 py-3 rounded-xl border border-gray-100 bg-gray-50">
                  <div className="flex items-start gap-3 min-w-0">
                    <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0 text-primary-600" />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {currentBranch?.name ?? `Пункт выдачи #${user.branch_id}`}
                      </p>
                      {currentBranch?.address && (
                        <p className="text-xs text-gray-500 truncate mt-0.5">
                          {currentBranch.address}
                        </p>
                      )}
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowBranchSelector((v) => !v)}
                    className="shrink-0 text-primary-600 hover:text-primary-700"
                  >
                    {showBranchSelector ? 'Скрыть' : 'Сменить'}
                  </Button>
                </div>

                {showBranchSelector && (
                  <div className="mt-4">
                    <BranchSelector
                      currentBranchId={user.branch_id}
                      onBranchChanged={handleBranchChanged}
                    />
                  </div>
                )}
              </>
            )}
          </Card.Body>
        </Card>
      </div>

      <div className="mt-6">
        <Button
          variant="ghost"
          onClick={handleLogout}
          className="text-red-500 hover:bg-red-50 hover:text-red-600"
        >
          <LogOut className="w-4 h-4" />
          Выйти из аккаунта
        </Button>
      </div>
    </div>
  );
};

export default ProfilePage;
